import axios from "axios";
import { isTokenExpiringsoon } from "./jwtUtils";
import { API_BASE_URL } from "./apiBaseUrl";
import { useAuthStore } from "../stores/authStore";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Flag pour éviter les rafraîchissements concurrents
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

const redirigerVersLoginSiNecessaire = (): void => {
  // Eviter les boucles de redirection si on est deja sur la page de login.
  if (window.location.pathname.startsWith("/login")) return;

  // On force une navigation "hard" pour reinitialiser proprement l'app (store Zustand, Cesium, etc.).
  window.location.href = "/login";
};

/**
 * Rafraîchir le token en utilisant le refresh token
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const token = await useAuthStore.getState().refreshAuth();
    if (!token) {
      redirigerVersLoginSiNecessaire();
    }
    return token;
  } catch (error) {
    console.error("Erreur lors du rafraîchissement du token:", error);
    useAuthStore.getState().logout();
    redirigerVersLoginSiNecessaire();
    return null;
  }
}

// Intercepteur pour ajouter le token JWT
apiClient.interceptors.request.use(async (config) => {
  // Important: ne jamais forcer "application/json" si on envoie un FormData.
  // Sinon Axios n'ajoute pas le bon "multipart/form-data; boundary=..." et Multer
  // ne voit aucun fichier (`req.file` undefined).
  const isFormData =
    typeof FormData !== "undefined" && config.data instanceof FormData;

  if (isFormData) {
    // Laisser Axios définir automatiquement le Content-Type + boundary.
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete (config.headers as Record<string, unknown>)["Content-Type"];
  } else if (!config.headers?.["Content-Type"]) {
    config.headers = config.headers ?? {};
    config.headers["Content-Type"] = "application/json";
  }

  let token = useAuthStore.getState().token;

  // Vérifier si le token va expirer bientôt et le rafraîchir si nécessaire
  if (token && isTokenExpiringsoon(token, 5)) {
    try {
      // Éviter les rafraîchissements concurrents
      if (isRefreshing) {
        // Attendre la completion du rafraîchissement en cours
        const newToken = await refreshPromise;
        if (newToken) {
          token = newToken;
        }
      } else {
        isRefreshing = true;
        refreshPromise = (async () => {
          const newToken = await refreshAccessToken();
          isRefreshing = false;
          refreshPromise = null;
          return newToken;
        })();

        token = await refreshPromise;
      }
    } catch (error) {
      console.error(
        "Erreur lors du rafraîchissement anticipé du token:",
        error,
      );
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Intercepteur pour gérer les erreurs d'authentification
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Si c'est une erreur 401 et qu'on n'a pas encore réessayé.
    // Convention: 401 = non authentifié (token manquant/invalide/expiré).
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Essayer de rafraîchir le token
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Réessayer la requête originale avec le nouveau token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error("Impossible de rafraîchir le token:", refreshError);
        useAuthStore.getState().logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // Si c'est une erreur 401, le backend considère l'utilisateur comme non authentifié.
    // Exemple: rotation du JWT_SECRET en prod => tokens existants invalides.
    if (status === 401) {
      useAuthStore.getState().logout();
      redirigerVersLoginSiNecessaire();
    }

    return Promise.reject(error);
  },
);

export default apiClient;
